/**
 * Badge kesehatan alat di Daftar Alat (ADR-0055 Fase 3) — logika MURNI, teruji di Node.
 *
 * Diturunkan dari kejadian alert TERBUKA seluruh armada (satu panggilan `GET /alert-events?open=true`)
 * digabung peta severity dari aturan (`GET /alert-rules`). Sengaja HANYA menandai alat yang punya
 * kejadian terbuka: ketiadaan alert BUKAN klaim "sehat" (alat tanpa agen juga tak punya alert), jadi
 * alat tanpa kejadian tidak diberi badge — statusnya yang sebenarnya hanya diketahui dari panel
 * "Agen telemetri" per alat, bukan dari daftar ini.
 *
 * Severity (Kritis vs Perhatian) tidak ada pada kejadian; ia milik aturan (`alertRuleId`). Kejadian
 * `offline` tak punya aturan (alertRuleId null) dan selalu menang sebagai tanda paling mendesak.
 */
import type { AlertEventResponse, AlertRuleResponse } from '../api/types';

export type BadgeTone = 'warn' | 'crit' | 'off';

export interface BadgeAlat {
  readonly tone: BadgeTone;
  readonly label: string;
}

/** Peta id-aturan → severity, untuk menilai kegentingan kejadian ambang. */
export function severitasAturan(
  rules: readonly AlertRuleResponse[],
): ReadonlyMap<string, AlertRuleResponse['severity']> {
  return new Map(rules.map((r) => [r.id, r.severity]));
}

/** Kelompokkan kejadian (terbuka) per alat, agar pemanggil tak mencari-cari dalam render. */
export function kejadianPerAlat(
  events: readonly AlertEventResponse[],
): ReadonlyMap<string, readonly AlertEventResponse[]> {
  const peta = new Map<string, AlertEventResponse[]>();
  for (const e of events) {
    const arr = peta.get(e.assetId);
    if (arr) arr.push(e);
    else peta.set(e.assetId, [e]);
  }
  return peta;
}

/**
 * Badge untuk satu alat dari kejadian terbukanya. null bila tak ada kejadian (tak diberi badge).
 * Prioritas: offline (paling mendesak) → kritis (ada aturan severity 'critical') → perhatian.
 * Tanpa peta severity (mis. tak punya izin baca aturan), kejadian ambang jatuh ke 'Perhatian'.
 */
export function badgeKesehatanAlat(
  eventsAlat: readonly AlertEventResponse[],
  severityByRule: ReadonlyMap<string, AlertRuleResponse['severity']> = new Map(),
): BadgeAlat | null {
  if (eventsAlat.length === 0) return null;
  if (eventsAlat.some((e) => e.kind === 'offline')) {
    return { tone: 'off', label: 'Offline' };
  }
  const adaKritis = eventsAlat.some(
    (e) => e.alertRuleId !== null && severityByRule.get(e.alertRuleId) === 'critical',
  );
  return adaKritis ? { tone: 'crit', label: 'Kritis' } : { tone: 'warn', label: 'Perhatian' };
}
